import { action } from '@ember/object'
import { service } from '@ember/service'
import Controller from '@ember/controller'
import { task } from 'ember-concurrency'
import StepUtils from 'teacher-ui-client/school-rollover/step-utils'
import type RouterService from '@ember/routing/router-service'
import type SchoolRolloverService from 'teacher-ui-client/services/school-rollover'
import type RolloverStudentImportService from 'teacher-ui-client/services/rollover-student-import'
import type { IntlService } from 'ember-intl'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { Log } from '@blakeelearning/log'
import type RolloverStepsRoute from 'teacher-ui-client/manage/rollover/steps/route'
import type SchoolRollover from 'teacher-ui-client/models/school-rollover'
import type { LogArgument } from '@blakeelearning/log/log-adapters/base'
import { RolloverStep } from 'teacher-ui-client/school-rollover/steps'

export default class StepsController extends Controller {
  @service router!: RouterService

  @service schoolRollover!: SchoolRolloverService

  @service rolloverStudentImport!: RolloverStudentImportService

  @service intl!: IntlService

  @service flashQueue!: FlashQueueService

  @service log!: Log

  declare model: ModelFor<RolloverStepsRoute>

  get title() {
    return StepUtils.getTitleFromRouteInfo(this.router.currentRoute)
  }

  get currentStepId() {
    return StepUtils.getStepFromRouteInfo(this.router.currentRoute)
  }

  get currentRollover() {
    return this.model.rollover
  }

  get stepItems(): { stepId: RolloverStep; stepLabel: string }[] {
    const homeStepLabel = this.intl.t(`manage.rollover.stepLabel`)
    const homeStepItem = { stepId: RolloverStep.Home, stepLabel: homeStepLabel }

    const rolloverStepItems = this.schoolRollover.steps.map((stepId) => {
      const stepLabel = this.intl.t(`manage.rollover.steps.${stepId}.stepLabel`)
      return { stepId, stepLabel }
    })

    return [homeStepItem, ...rolloverStepItems]
  }

  get isFirstStep() {
    return this.currentStepId && StepUtils.isFirstStep(this.currentStepId, this.schoolRollover.steps)
  }

  get isLastStep() {
    return this.currentStepId && StepUtils.isLastStep(this.currentStepId, this.schoolRollover.steps)
  }

  get hideFooter() {
    return StepUtils.getHideFooterFromRouteInfo(this.router.currentRoute)
  }

  get isImportingStudents() {
    return this.rolloverStudentImport.validateCsv.isRunning || this.rolloverStudentImport.importCsv.isRunning
  }

  get isSubmitting() {
    return this.completeRolloverTask.isRunning
  }

  completeRolloverTask = task(async (rollover: SchoolRollover) => {
    try {
      await this.schoolRollover.complete(rollover)
      this.showSuccess()
      this.goToRolloverHome()
    } catch (e) {
      this.showError()
      this.log?.error(`Could not finish rollover w/id: ${rollover.id}`, e as LogArgument)
    }
  })

  showSuccess() {
    const msg = this.intl.t('manage.rollover.completeSuccessMsg')
    this.flashQueue.addSuccess({ subtitle: msg })
  }

  showError() {
    const msg = this.intl.t('manage.rollover.completeErrorMsg')
    this.flashQueue.addFail({ subtitle: msg })
  }

  goToRolloverHome() {
    void this.router.transitionTo('manage.rollover.wizard')
  }

  @action
  next() {
    const nextStep = this.currentStepId && this.schoolRollover.getNextStep(this.currentStepId)
    if (nextStep) {
      const routeName = StepUtils.getRouteNameForStep(nextStep)
      void this.router.transitionTo(routeName)
    }
  }

  @action
  previous() {
    if (this.isFirstStep) {
      this.goToRolloverHome()
    } else {
      const fromStep = this.currentStepId
      const toStep = fromStep && this.schoolRollover.getPreviousStep(fromStep)
      if (toStep) {
        const routeName = StepUtils.getRouteNameForStep(toStep)
        void this.router.transitionTo(routeName)
      }
    }
  }

  @action
  goToStep(stepId: RolloverStep) {
    if (!stepId || stepId === RolloverStep.Home) {
      this.goToRolloverHome()
    } else if (this.currentRollover && this.schoolRollover.canTransitionToStep(this.currentRollover, stepId)) {
      const routeName = StepUtils.getRouteNameForStep(stepId)
      void this.router.transitionTo(routeName)
    }
  }
}
